import styled from '@emotion/styled'
import { useContext, useMemo, type ElementType } from 'react'

import { DefaultBlogCard, ModernBlogCard } from '@/legacy/components/BlogCard'
import { Button } from '@/legacy/components/Button'
import { Container, type Space } from '@/legacy/components/Container'
import { Icon } from '@/legacy/components/Icon'
import { ScreenReaderNotifier } from '@/legacy/components/ScreenReaderNotifier'
import { ScrollableList } from '@/legacy/components/ScrollableList/ScrollableList'
import { HeadingM } from '@/legacy/components/Typography'

import { ArticlesContext } from '@/legacy/providers'

import { CONFIG } from '@/ui/shared/styles'

interface Props {
  title?: {
    text?: string
    tag?: ElementType
  }
  layout?: 'mostViewed' | 'default'
  space?: Space
  showMore?: boolean
  isSlider?: boolean
}

const BlogListStyles = styled.div<Pick<Props, 'layout'>>(
  `
  display: grid;
  gap: 1rem;
  height: auto;

  .blog-card__infos{
    margin-top: 0.5rem;

    span:first-of-type {
      margin-right: 1rem;
    }
  }

`,
  (props) =>
    props.layout === 'default' &&
    `
    grid-template-columns: repeat(var(--columns, 1), 1fr);

    ${CONFIG.media.md} {
      --columns: 2;
    }
    ${CONFIG.media.lg} {
      --columns: 3;
    }
`,
  (props) =>
    props.layout === 'mostViewed' &&
    `
    grid-template-columns: repeat(1, 1fr);

    ${CONFIG.media.md} {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      grid-template-areas:
        'large .'
        'large .';

      .blog-list__large {
        grid-area: large;
      }
    }
  `,
)

const SkeletonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`

const getLayoutCart = ({ layout, index }: Pick<Props, 'layout'> & { index: number }) =>
  layout == 'mostViewed' ? (index === 0 ? 'large' : 'horizontal') : 'default'

const BlogList = ({
  title = { tag: 'p' },
  layout = 'default',
  showMore = false,
  space = 'm',
  isSlider = false,
}: Props) => {
  const { articlesListState, handleShowMore, perPage } = useContext(ArticlesContext)
  const { list, hasMore, loading, firstNewPostIndex } = articlesListState

  const CardComponent = isSlider ? ModernBlogCard : DefaultBlogCard
  const BlogListComponent = isSlider ? ScrollableList : BlogListStyles

  const LoadingCards = useMemo(
    () =>
      [...Array(perPage)].map((_v, index) => (
        <CardComponent.Loading
          key={`bli-load-${index}`}
          modifier={getLayoutCart({ layout, index })}
        />
      )),
    [layout, perPage, CardComponent],
  )

  if (!list.length) {
    return null
  }

  return (
    <Container spaceOut={space}>
      <HeadingM className="eu-sm-3" tag={title.tag as string}>
        {title.text}
      </HeadingM>
      <BlogListComponent className="eu-sm-2" layout={layout} as={isSlider ? 'ol' : 'div'}>
        {list.map((item, index) => {
          const { article, url, category, product, customPublication } = item.data
          const date = customPublication?.articleDate || item.createdDate
          const { image } = article
          const modifier = getLayoutCart({ layout, index })

          const isFirstPostRender = index === 0
          const isFirstNewPost = !isFirstPostRender && index === firstNewPostIndex

          return (
            <CardComponent
              forceFocus={isFirstNewPost}
              key={item.id}
              url={url}
              title={article.title}
              image={image.small}
              alt={image.alt}
              product={product}
              readingTime={customPublication?.readingTime}
              category={category && category.value?.data}
              lastUpdated={item.lastUpdated || item.createdDate}
              date={date}
              className={(modifier === 'large' && 'blog-list__large') || undefined}
              modifier={modifier}
            />
          )
        })}
      </BlogListComponent>

      <ScreenReaderNotifier
        label="Por favor, aguarde enquanto carregamos mais artigos."
        enabled={loading}
      >
        <SkeletonWrapper>{LoadingCards}</SkeletonWrapper>
      </ScreenReaderNotifier>

      {showMore && hasMore && (
        <Button
          type="button"
          size="lg"
          modifier="ghost"
          className="eu-fx-center"
          onClick={handleShowMore}
          disabled={loading}
          aria-label={loading ? 'Carregando mais artigos' : 'Carregar mais artigos'}
        >
          {loading ? (
            'Carregando...'
          ) : (
            <>
              <Icon icon="circle-plus" space="right" />
              Carregar mais
            </>
          )}
        </Button>
      )}
    </Container>
  )
}

export default BlogList
